import dayjs from "dayjs";
import { DetailedHTMLProps, HTMLAttributes, useContext } from "react";
import { BsFillPersonFill } from "react-icons/bs";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";
import tw from "twin.macro";
import { usePost, usePostValue } from "../..";
import { apiRoutes, BASE_URL } from "../../../../api";
import browserRoutes from "../../../../common/browserRoutes";
import styles from "../../../../common/styles";
import { DP } from "../../../../common/types";
import DeleteButton from "../../../../components/DeleteButton";
import { ProfileContext } from "../../../../context/profileContext";
import { useStateReducer } from "../../../../hooks/useStateReducer";
import CloudinaryUploadButton from "../../../../pages/CreatePost/components/CloudinaryUploadButton";
import { fetchDeletePost } from "../../api";
import Video from "./components/CloudinaryVideoPlayer";
import CommentsButton from "./components/CommentsButton";
import LikeButton from "./components/LikeButton";
import SubscribeButton from "./components/SubscribeButton";

interface Props extends DP {}

const Content = (props: Props) => {
  const [state, setState] = useStateReducer({
    isLoading: false,
  });

  const profile = useContext(ProfileContext);

  const { set: setPostContext, get: getPost } = usePost();
  // const post = usePostValue((s) => s.post);
	const topics = usePostValue((s) => s.post.topics)
	const createdById = usePostValue((s) => s.post.createdBy._id)
	const createdByUsername = usePostValue((s) => s.post.createdBy.username)

  const handleDeletePost = () => {
    setState({ isLoading: true });
    fetchDeletePost(getPost().post._id).then(() => {
      setState({ isLoading: false });
      setPostContext({ isDeleted: true });
    });
  };

  return (
    <Wrapper className={props.className}>
      <Title />
      <AuthorPictureWrapper>
        <AuthorPictureLink relative="path" to={"../" + browserRoutes.user(createdByUsername)}>
          <img
            src={getPost().post.createdBy.picture}
            alt={"user_picture"}
          ></img>
        </AuthorPictureLink>
        <SubscribersCnt />
      </AuthorPictureWrapper>
      <Topics>
        {topics.map((el) => (
          <Topic key={el.title}>{el.title}</Topic>
        ))}
      </Topics>
			<Video />
      <UsernameWrapper>
        <UsernameLink to={"../" + browserRoutes.user(createdByUsername)}>
          {createdByUsername}
        </UsernameLink>
        {profile?.user._id != createdById && <SubscribeButton />}
      </UsernameWrapper>
      <CreatedAt>
        Uploaded: {dayjs(getPost().post.createdAt).format("DD.MM.YYYY")}
      </CreatedAt>
      <ButtonsWrapper>
        {profile?.user?._id == createdById && (
          <DeleteButton
            disabled={state.isLoading}
            onConfirm={handleDeletePost}
          />
        )}
        <LikeButton />
        <CommentsButton />
      </ButtonsWrapper>
    </Wrapper>
  );
};


const Title = styled(() => {
  const title = usePostValue((s) => s.post.title);
  return <UTitle>{title}</UTitle>;
})``;


const SubscribersCnt = () => {
  const subsCount = usePostValue((s) => s.post.createdBy.subsCount);
  return (
    <SubscribersCount>
      <span>{subsCount}</span>
      <BsFillPersonFill />
    </SubscribersCount>
  );
};
const Wrapper = styled.div(() => [
  tw`flex flex-col`,
	tw`w-full md:(w-[16rem])`,
  css`
    display: grid;
    gap: var(--gap);
    grid-template:
      "picture username subBtn"
      "picture title title"
      ". topics cAt"
      ". video video"
      ". btnsWrapper btnsWrapper"
      / var(--user-picture-column-width) 1fr auto;

    ${DeleteButton} {
      ${tw`mr-auto`}
    }

    @media (hover: hover) {
      ${ButtonsWrapper} > * {
        opacity: 0.3;
      }
      &:hover ${ButtonsWrapper} > * {
        opacity: 1;
      }
      ${ButtonsWrapper} > *:hover {
        ${styles.ring}
      }
    }

    ${UTitle} {
      grid-area: title;
    }
    ${Topics} {
      grid-area: topics;
    }
    ${Video} {
      grid-area: video;
    }
    ${AuthorPictureLink} {
      grid-area: picture;
    }
    ${UsernameLink} {
      grid-area: username;
    }
    ${SubscribeButton} {
      grid-area: subBtn;
      ${tw`text-[0.3rem]`}
    }
    ${CreatedAt} {
      grid-area: cAt;
    }
    ${ButtonsWrapper} {
      grid-area: btnsWrapper;
    }
  `,
]);

const ButtonsWrapper = styled.div(() => [
  tw`flex items-center gap-x-1 justify-end px-1 h-6 relative bottom-[var(--gap)] mb-[-var(--gap)]`,
  css`
    border-style: none solid solid solid;
    border-top-left-radius: 0;
    border-top-right-radius: 0;

    & > * {
      ${tw`text-[0.5rem] text-pink-400`}
    }
  `,
]);

const UTitle = styled.h3(() => [
  css`
    font-size: 0.45rem;
  `,
]);

const Topics = styled.div(() => [
  tw`flex space-x-0.5`,
  css`
    font-size: 0.3rem;
  `,
]);

const Topic = styled.div(() => [
  css`
    text-decoration: underline;
    &::before {
      content: "#";
    }
  `,
]);

const AuthorPictureWrapper = styled.div(() => [tw`space-y-0.5 relative`]);

const SubscribersCount = styled.div(() => [
  tw`text-pink-400 absolute left-0 right-0 bottom-0 flex items-center w-min space-x-[0.1em] text-[0.35rem] mx-auto border-[0.001rem] border-pink-400 rounded-3xl p-0.5 px-1`,
  css`
    transform: translateY(120%);
    svg {
      margin-bottom: -0.035rem;
      font-size: 0.9em;
    }
  `,
]);

const AuthorPictureLink = styled(Link)(() => [
  css`
    margin: auto 0;
    aspect-ratio: 1/1;
    border-radius: 50%;
    overflow: hidden;
    ${tw`flex items-center justify-center w-full h-auto`}

    img {
      width: auto;
      object-fit: cover;
      height: 100%;
    }
  `,
]);

const UsernameWrapper = styled.div(() => [tw`flex items-center space-x-1`]);

const UsernameLink = styled(Link)(() => [
  tw`text-[0.5rem] font-[500] hover:(underline)`,
]);

const CreatedAt = styled.p(() => [tw`italic opacity-20 text-[0.35rem]`]);

export default styled(Content)``;
